import { Component } from '@angular/core';
import * as moment from 'moment-timezone';

import {BaseComponent} from "../../views/base.component";
import {FilterFormService} from "../../views/filter_form/filter_form.service";
import {TranslateService} from "../../translate/translate.service";
import {EventsService} from "../../lib/EventsService";
import {ActivatedRoute, Router} from "@angular/router";
import {ChartParams} from "../../views/chart/chart.component";
import {ModalService} from "../../views/modal/base_modal.component";
import {FilterParams} from "../../models/filter_form.model";
import {DepartedTableDetail} from "./departed_table_detail";
import {BuyingChartDetail} from "./BuyingChartDetail";
import {HelperService} from "../../lib/helper.service";
import { DomainsService } from "../domains.service";
import { IStatResult } from "../../models/responce_result.model";

interface IExpiredDomainDepartedStats extends IStatResult {
	comebackTotal : number;
	comeback      : string[];
	departedTotal : number;
	departed      : object[]
}

@Component({
	selector: 'app-expired-domains',
	templateUrl: './expired-domains.component.html'
})
export class ExpiredDomainsComponent extends BaseComponent {
	addFilterParams = {
		userType: 'user',
		period: 'month'
	};
	
	// absolute relative
	chartGroupType: string = 'absolute';
	
	buyingChart = new ChartParams({
		groups        : [ 'Active', 'Expired', 'Free', 'Renew', 'Removed' ]
	});
	
	attachChart = new ChartParams();
	attachChartExcludeFreeZones = true;

	realAliveChart = new ChartParams({
		chartType : 'bar'
	});
	
	sslChart = new ChartParams();
	
	departedChart = new ChartParams();
	
	departedTable = {
		comebackTotal : 0,
		comeback      : [],
		departedTotal : 0,
		departed      : []
	};
	
	tableType: string = 'departed';

	constructor(
		public eventsService: EventsService,
		public activatedRoute: ActivatedRoute,
		public translate: TranslateService,
		public router: Router,
		public filter: FilterFormService,
		public modalService: ModalService,
		public helper: HelperService,
		public domainsService: DomainsService,
	) {
		super(eventsService, domainsService, activatedRoute, filter, translate);

		const me = this;

		me.buyingChart.setOptions({
			title: me.__a('billing.expired_domains.bought from us'),
			names: {
				Attach     : me.__a('billing.expired_domains.Attach'),
				Disconnect : me.__a('billing.expired_domains.Disconnect'),
				Left       : me.__a('billing.expired_domains.Left'),
				Park       : me.__a('billing.expired_domains.Park'),
				Dead       : me.__a('billing.expired_domains.Dead'),
				Alive      : me.__a('billing.expired_domains.Alive'),
				RealAlive  : me.__a('billing.expired_domains.RealAlive')
			},
			colors: {
				'Active'  : '#1f77b4',
				'Expired' : '#ffa500',
				'Free'    : '#ff0000',
				'Renew'   : '#008000',
				'Removed' : '#8C8C8C'
			},
			chartType: 'bar',
			
			events: {
				onclick: function(e) {
					const
						index = e.index + 1,
						dates = me.buyingChart.data[0];
					
					me.showBuyingChartDetail(moment(dates[index]).format('YYYY-MM-DD'));
				}
			}
		});

		me.attachChart.setOptions({
			title: me.__a('billing.expired_domains.Attach and Disconnect'),
			names: {
				Attach     : me.__a('billing.expired_domains.Attach'),
				Disconnect : me.__a('billing.expired_domains.Disconnect'),
				Left       : me.__a('billing.expired_domains.Left'),
				Park       : me.__a('billing.expired_domains.Park'),
				Dead       : me.__a('billing.expired_domains.Dead'),
				Alive      : me.__a('billing.expired_domains.Alive'),
				RealAlive  : me.__a('billing.expired_domains.RealAlive')
			},
			showLabels: [
				'Attach', 'Disconnect', 'Alive', 'RealAlive'
			],
			axes: {
				Alive: 'y',
				Attach: 'y2',
				Disconnect: 'y2',
				Left: 'y2',
				Park: 'y2',
				Dead: 'y2',
				RealAlive: 'y2'
			},
			axis: {
				y2: {
					show: true
				}
			}
		});

		me.realAliveChart.setOptions({
			title: me.__a('billing.expired_domains.RealAlive'),
			names: {
				RealAlive  : me.__a('billing.expired_domains.RealAlive')
			},
			colors: {
				RealAlive: '#e377c2'
			}
		});

		me.sslChart.setOptions({
			title: me.__a('billing.expired_domains.SSL'),
			names: {
				Attach: me.__a('billing.expired_domains.SSL')
			}
		});

		me.departedChart.setOptions({
			title: me.__a('billing.expired_domains.history departed domains stats'),
			names: {
				Attach     : me.__a('billing.expired_domains.Attach'),
				Disconnect : me.__a('billing.expired_domains.Disconnect'),
				Left       : me.__a('billing.expired_domains.Left'),
				Park       : me.__a('billing.expired_domains.Park'),
				Dead       : me.__a('billing.expired_domains.Dead'),
				Alive      : me.__a('billing.expired_domains.Alive'),
				RealAlive  : me.__a('billing.expired_domains.RealAlive')
			},
			chartType : 'bar',
			groups    : [ 'Left', 'Park', 'Dead' ],
			colors    : {
				'Left'   : '#ffa500',
				'Park'   : '#1f77b4',
				'Dead'   : '#ff0000'
			},
		});
	}

	async getData() {
		this.attachChartExcludeFreeZones = this.filter.filterParams['attachChartExcludeFreeZones'] ? this.filter.filterParams['attachChartExcludeFreeZones'] : true;

		await this.getBuyingChart();
		await this.getAttachChart();
		await this.getRealAliveChart();
		await this.getSslChart();
		await this.getDepartedChart();
		await this.getDepartedTable();
	}

	async getBuyingChart() {
		this.buyingChart.showLoader();
		const { charts } = await this.domainsService.expired.chart(
			this.filter.filterParams,
			'buying_chart'
		).toPromise();
		this.buyingChart.data = charts;
		
		this.buyingChart.hideLoader();
	}

	async excludeFreeZonesCheckChange() {
		await this.getAttachChart();
	}

	async getAttachChart() {
		this.filter.filterParams['attachChartExcludeFreeZones'] = this.attachChartExcludeFreeZones;

		this.attachChart.showLoader();
		const { charts } = await this.domainsService.expired.chart(
			this.filter.filterParams,
			'attach/chart'
		).toPromise();
		this.attachChart.data = charts;
		this.attachChart.hideLoader();
	}

	async getRealAliveChart() {
		this.realAliveChart.showLoader();
		const { charts } = await this.domainsService.expired.chart(
			this.filter.filterParams,
			'real_alive/chart'
		).toPromise();
		this.realAliveChart.data = charts;
		this.realAliveChart.hideLoader();
	}

	async getSslChart() {
		this.sslChart.showLoader();
		const { charts } = await this.domainsService.expired.chart(
			this.filter.filterParams,
			'ssl/chart'
		).toPromise();
		this.sslChart.data = charts;
		this.sslChart.hideLoader();
	}

	async getDepartedChart() {
		this.departedChart.showLoader();
		const { charts } = await this.domainsService.expired.chart(
			this.filter.filterParams,
			'departed/chart'
		).toPromise();
		this.departedChart.data = charts;
		this.departedChart.hideLoader();
	}

	async getDepartedTable() {
		this.departedTable = await this.domainsService.expired.stats<IExpiredDomainDepartedStats>(
			this.filter.filterParams,
			'departed/stats'
		).toPromise();
	}

	async showBuyingChartDetail(date) {
		let me = this;
		
		const modalRef = me.modalService.open(BuyingChartDetail, {
			title     : date,
			isLoad    : true,
		}, {size: 'hg'});

		const { stats } = await me.domainsService.expired.detail(
			me.filter.getCopyFilterParams(<FilterParams>{ date, }),
			'buying_chart/detail'
		).toPromise();
		modalRef.componentInstance.stats = stats;
		modalRef.componentInstance.isLoad = false;
	}

	async showDepartedTableDetail(stat) {
		const modalRef = this.modalService.open(DepartedTableDetail, {
			title     : stat.moveTo,
			isLoad    : true,
			tableType : this.tableType
		}, {size: 'hg'});
		
		const { stats } = await this.domainsService.expired.detail(
			this.filter.getCopyFilterParams(<FilterParams>{
				ns        :    stat.moveTo,
				tableType : this.tableType
			}),
			'moveto_detail'
		).toPromise();
		modalRef.componentInstance.stats = stats;
		
		modalRef.componentInstance.isLoad = false;
	}
}
